import {useRef, useState} from 'react';
import styled from 'styled-components';
import {Button} from '@/components/Button';
import {DropdownIcon} from '@/components/Icons';
import {Menu, MenuItem} from '@/components/Menu';
import {useBackgroundClick} from '@/hooks/backdrop';
import {Flexbox} from '@/styled-components';
import {interceptProps} from '@/styled-components/utils';

interface ISort {
  name: string;
  sort: string;
}

interface IProps {
  disabled?: boolean;
  defaultSort?: ISort;
  options: ISort[];
  onChange(nextSort: string): void;
}

export const SortMenu = ({
  disabled,
  defaultSort,
  options,
  onChange,
}: IProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  useBackgroundClick(menuRef, () => setOpen(false), [btnRef]);

  const hasOptions = options?.length > 1;

  const menuOpen = () => {
    if (hasOptions) {
      setOpen((prev) => !prev);
    }
  };

  const onClick = (type: ISort['sort']) => {
    if (!type) {
      return;
    }

    if (type !== defaultSort?.sort) {
      onChange(type);
    }

    setOpen(false);
  };

  if (!options?.length) {
    return;
  }

  const currentSort = defaultSort ?? options[0];

  return (
    <Root>
      <Button
        variant="ghost"
        color="black"
        size="small"
        disabled={disabled}
        style={hasOptions ? {} : {cursor: 'default'}}
        ref={btnRef}
        onClick={menuOpen}
      >
        {currentSort.name}
        <Arrow rotate={open} disabled={disabled} src={DropdownIcon} />
      </Button>
      {open && (
        <Menu ref={menuRef}>
          {options.map((i) => (
            <MenuItem
              key={i.sort}
              active={i.sort === currentSort.sort}
              onClick={() => onClick(i.sort)}
            >
              {i.name}
            </MenuItem>
          ))}
        </Menu>
      )}
    </Root>
  );
};

const Root = styled(Flexbox)`
  position: relative;
  ${Menu} {
    position: absolute;
    right: 0;
    top: 100%;
    z-index: 1;
  }
  ${MenuItem} {
    text-align: left;
  }
  ${Button} {
    padding: 2px;
    display: flex;
    align-items: center;
  }
`;

const Arrow = styled('img').withConfig({
  shouldForwardProp: interceptProps(['rotate', 'disabled']),
})<{
  rotate?: boolean;
  disabled?: boolean;
}>`
  width: 16px;
  height: 16px;
  ${({rotate}) => rotate && `transform: rotate(180deg);`}
  ${({disabled}) => (disabled ? `opacity: 0.3;` : `cursor: pointer;`)}
`;
